/* eslint-disable no-self-assign */
/**
 * Version Manager - Handles version-based cache invalidation for browser resources
 */

const VERSION_KEY = "app_version";
// Written by src/cpip-build-guard.js right before it reloads the page (P81-148827).
const BUILD_GUARD_RELOAD_KEY = "cpipBuildGuardReload";
const BUILD_GUARD_RELOAD_WINDOW_MS = 60 * 1000;

/**
 * Gets the current app version from environment variable
 * @returns {string|null} The version string or null if not found
 */
function getCurrentVersion() {
  return process.env.REACT_APP_GIT_TAG || null;
}

/**
 * Gets the stored version from localStorage
 * @returns {string|null} The stored version or null
 */
function getStoredVersion() {
  try {
    return window.localStorage.getItem(VERSION_KEY);
  } catch {
    return null;
  }
}

/**
 * Stores the current version in localStorage
 * @param {string} version - The version to store
 */
function storeVersion(version) {
  try {
    window.localStorage.setItem(VERSION_KEY, version);
  } catch (e) {
    console.warn("[VersionManager] Failed to store version:", e);
  }
}

/**
 * Whether cpip-build-guard reloaded this tab moments ago. That reload already loaded
 * the current build, so a hard reload here would only repeat it.
 * @returns {boolean}
 */
function reloadedByBuildGuard() {
  try {
    const last = JSON.parse(window.sessionStorage.getItem(BUILD_GUARD_RELOAD_KEY) || "null");
    return !!last && Date.now() - last.at < BUILD_GUARD_RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Should force a hard reload to fetch fresh resources from the server
 * Safest, production-ready client-only solution to force the browser to fetch fresh resources
 */
async function forceHardReload() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now());
    window.location.replace(url.toString());
  } catch (error) {
    // Fallback to simple reload
    window.location.reload();
  }
}

/**
 * Checks for version changes and handles cache invalidation
 * If a version change is detected, triggers a hard reload
 */
export function checkAndHandleVersionChange() {
  const currentVersion = getCurrentVersion();

  if (!currentVersion) {
    return;
  }

  const storedVersion = getStoredVersion();

  if (!storedVersion) {
    storeVersion(currentVersion);
    return;
  }

  if (storedVersion !== currentVersion) {
    storeVersion(currentVersion);
    if (!reloadedByBuildGuard()) forceHardReload();
  }
}

/**
 * Initializes the version manager
 * Should be called early in the application lifecycle
 */
export function initVersionManager() {
  checkAndHandleVersionChange();
}
